import type {
  ActionSpec,
  ConsiderationDef,
  PrimitiveRef,
  SimulationDef,
} from "../types";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Raw = Record<string, any>;

/** Coerce to a finite number or fall back — a NaN would serialize as null and silently
 * break determinism server-side (an explicit null seed defeats from_dict's default). */
function num(value: unknown, fallback: number): number {
  const n = Number(value ?? fallback);
  return Number.isFinite(n) ? n : fallback;
}

function arr(value: unknown): Raw[] {
  return Array.isArray(value) ? (value as Raw[]) : [];
}

function ref(raw: any): PrimitiveRef {
  return { name: String(raw?.name ?? ""), params: (raw?.params ?? {}) as Record<string, unknown> };
}

function consideration(raw: Raw): ConsiderationDef {
  return {
    name: String(raw.name ?? ""),
    input: ref(raw.input),
    curve: { kind: String(raw.curve?.kind ?? "linear"), params: raw.curve?.params ?? {} },
    weight: num(raw.weight, 1),
  };
}

function action(raw: Raw): ActionSpec {
  return {
    verb: String(raw.verb ?? "IDLE"),
    eligible: ref(raw.eligible ?? { name: "always" }),
    effect: arr(raw.effect).map(ref),
    considerations: arr(raw.considerations).map(consideration),
    base_weight: num(raw.base_weight, 1),
    build_params: raw.build_params ? ref(raw.build_params) : null,
    duration: num(raw.duration, 1),
    interruptible: Boolean(raw.interruptible ?? true),
    tick_effect: arr(raw.tick_effect).map(ref),
  };
}

/** Fill every schema default (mirrors SimulationDef.from_dict) so the form can rely on shape. */
export function normalizeDefinition(raw: Raw): SimulationDef {
  return {
    name: String(raw.name ?? "untitled"),
    seed: num(raw.seed, 0),
    space: {
      kind: String(raw.space?.kind ?? "graph"),
      topology: raw.space?.topology ?? {},
      position_component: String(raw.space?.position_component ?? "node"),
      perception_radius: num(raw.space?.perception_radius, 0),
    },
    components: arr(raw.components).map((c: Raw) => ({
      name: String(c.name ?? ""),
      kind: String(c.kind ?? "array"),
      dtype: String(c.dtype ?? "float64"),
      fill: num(c.fill, 0),
      double_buffered: Boolean(c.double_buffered ?? true),
      object_kind: c.object_kind ?? null,
      capacity: c.capacity ?? null,
    })),
    fields: arr(raw.fields).map((f: Raw) => ({
      name: String(f.name ?? ""),
      dtype: String(f.dtype ?? "float64"),
      fill: num(f.fill, 0),
      initial: f.initial ?? {},
    })),
    item_types: arr(raw.item_types).map((t: Raw) => ({
      name: String(t.name ?? ""),
      affords: Array.isArray(t.affords) ? t.affords.map(String) : [],
      consumable: Boolean(t.consumable ?? false),
      value: num(t.value, 0.1),
      max_condition: num(t.max_condition, 1),
      properties: t.properties ?? {},
    })),
    agents: {
      count: num(raw.agents?.count, 1),
      placement: arr(raw.agents?.placement).map(ref),
    },
    actions: arr(raw.actions).map(action),
    world_systems: arr(raw.world_systems).map(ref),
    perception: arr(raw.perception).map(ref),
    metrics: arr(raw.metrics).map((m: Raw) => ({
      name: String(m.name ?? ""),
      metric: ref(m.metric ?? {}),
      every: num(m.every, 1),
    })),
    policy: { kind: String(raw.policy?.kind ?? "utility"), params: raw.policy?.params ?? {} },
    arbitrator: {
      kind: String(raw.arbitrator?.kind ?? "passthrough"),
      params: raw.arbitrator?.params ?? {},
    },
    execution: raw.execution
      ? {
          component: String(raw.execution.component ?? "execution"),
          commitment_bonus: num(raw.execution.commitment_bonus, 0.1),
        }
      : null,
    lifecycle: raw.lifecycle ? { component: String(raw.lifecycle.component ?? "alive") } : null,
  };
}

export function serializeDefinition(definition: SimulationDef): string {
  return JSON.stringify(definition, null, 2);
}

export function parseDefinition(text: string): SimulationDef {
  return normalizeDefinition(JSON.parse(text) as Raw);
}

export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64) || "untitled"
  );
}
